/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectTextFieldN.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { componentStyle } from "../../config/appTheme";

function SelectTextFieldN({
  size,
  label,
  variant,
  required,
  disabled,
  optionsList,
  selectedValue,
  sltFldStyle,
  handleOnChange,
}) {
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 220,
        overflowX: "hidden", // Hide horizontal overflow
        scrollbarWidth: "thin", // Firefox
        msOverflowStyle: "none", // IE and Edge
        "&::-webkit-scrollbar": {
          height: "8px", // Adjust scrollbar height
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888", // Scrollbar thumb color
          borderRadius: "4px", // Scrollbar thumb rounded corners
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1", // Scrollbar track color
        },
      },
    },
  };

  const [value, setValue] = React.useState(selectedValue);

  useEffect(() => {

    setValue(selectedValue);
  }, [selectedValue]);

  return (
    <FormControl
      variant={variant}
      fullWidth
      size={size}
      required={required}
      sx={{
        ...componentStyle.form.select.FormControl,
      }}
    >
      <InputLabel
        shrink={selectedValue == 0 ? true : !!selectedValue}
        sx={{ ...componentStyle.form.select.label }}
      >
        {label}
      </InputLabel>
      <Select
        disabled={disabled}
        value={value}
        onChange={handleOnChange}
        MenuProps={menuProps}
        sx={{ ...componentStyle.form.select.select }}
      >
        {optionsList?.map((option) => (
          <MenuItem
            key={option?.value}
            value={option?.value}
            sx={{
              ...componentStyle.form.select.menuItem,
            }}
          >
            <Box sx={{ display: "flex" }}>
              <Box flex={1}>{option?.label}</Box>

              {label === "Select Legal Standards" &&
                (option?.version ? (
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, color: "text.secondary", fontStyle: "italic" }}
                  >
                    v{option.version}
                  </Typography>
                ) : null)}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

SelectTextFieldN.propTypes = {
  size: PropTypes.string,
  label: PropTypes.node,
  variant: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  optionsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.node,
      version: PropTypes.string,
    })
  ),
  selectedValue: PropTypes.any,
  sltFldStyle: PropTypes.object,
  handleOnChange: PropTypes.func,
};

export default SelectTextFieldN;
